/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Advanced Client-Side API Cache (In-Memory + SessionStorage + SWR)
 * ─────────────────────────────────────────────────────────────────────────────
 * Features:
 *   1. Stale-While-Revalidate (SWR): Returns cached data instantly in 0ms,
 *      then triggers background revalidation when slightly stale.
 *   2. SessionStorage Persistence: Master data (/hospitals, /roles, /services)
 *      survives F5 / page reloads with 0 network calls.
 *   3. In-flight Request Deduplication: Merges identical concurrent calls.
 *   4. Auto-Invalidation on mutations (POST, PUT, PATCH, DELETE) & Socket events.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const STORAGE_PREFIX = 'crm_cache_';
const SWR_GRACE_PERIOD_MS = 10 * 60 * 1000; // 10 minutes grace period for SWR

class ApiCacheManager {
  constructor() {
    this.cache = new Map();
    this.inFlight = new Map();
    this.listeners = new Set();
    this.hydrateFromSessionStorage();
  }

  /**
   * Determine TTL (Time To Live in ms) based on URL pattern
   */
  getDefaultTTL(url = '') {
    const cleanUrl = url.toLowerCase();

    // 1. Master & static dropdown data (hospitals, roles, categories, services) -> 10 minutes
    if (
      cleanUrl.includes('/hospitals') ||
      cleanUrl.includes('/roles') ||
      cleanUrl.includes('/users?role=') ||
      cleanUrl.includes('/categories') ||
      cleanUrl.includes('/services')
    ) {
      return 10 * 60 * 1000;
    }

    // 2. User profile & settings -> 3 minutes
    if (cleanUrl.includes('/auth/profile') || cleanUrl.includes('/settings')) {
      return 3 * 60 * 1000;
    }

    // 3. Navigation badge counters & small totals (limit=1) -> 45 seconds
    if (
      cleanUrl.includes('limit=1') ||
      cleanUrl.includes('/onboarding/pending') ||
      cleanUrl.includes('/dashboard/stats')
    ) {
      return 45 * 1000;
    }

    // 4. General lists and search queries -> 20 seconds
    return 20 * 1000;
  }

  /**
   * Should this resource be saved in sessionStorage (survive F5)?
   */
  isPersistentResource(url = '') {
    const clean = url.toLowerCase();
    return (
      clean.includes('/hospitals') ||
      clean.includes('/roles') ||
      clean.includes('/users?role=') ||
      clean.includes('/categories') ||
      clean.includes('/services') ||
      clean.includes('/settings')
    );
  }

  /**
   * Hydrate in-memory cache from sessionStorage on startup
   */
  hydrateFromSessionStorage() {
    if (typeof window === 'undefined' || !window.sessionStorage) return;

    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) {
          const raw = sessionStorage.getItem(key);
          if (raw) {
            const entry = JSON.parse(raw);
            const cacheKey = key.replace(STORAGE_PREFIX, '');
            if (Date.now() - entry.timestamp < entry.ttl + SWR_GRACE_PERIOD_MS) {
              this.cache.set(cacheKey, entry);
            } else {
              sessionStorage.removeItem(key);
            }
          }
        }
      }
    } catch (e) {
      console.warn('[ApiCache] SessionStorage hydration skipped:', e.message);
    }
  }

  /**
   * Generate normalized cache key from URL & params
   */
  generateKey(url = '', params) {
    if (!params || Object.keys(params).length === 0) {
      return url;
    }
    try {
      const sortedParams = Object.keys(params)
        .sort()
        .map((k) => `${k}=${params[k]}`)
        .join('&');
      return url.includes('?') ? `${url}&${sortedParams}` : `${url}?${sortedParams}`;
    } catch {
      return url;
    }
  }

  /**
   * Retrieve cached data. Returns data if valid, or null if completely missing/dead.
   */
  get(key) {
    let item = this.cache.get(key);

    // Fallback to sessionStorage if missing in memory
    if (!item && typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const stored = sessionStorage.getItem(STORAGE_PREFIX + key);
        if (stored) {
          item = JSON.parse(stored);
          this.cache.set(key, item);
        }
      } catch {}
    }

    if (!item) return null;

    const age = Date.now() - item.timestamp;
    // Dead beyond TTL + grace period
    if (age > item.ttl + SWR_GRACE_PERIOD_MS) {
      this.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * SWR query: returns data and whether it is stale (needs background revalidation)
   */
  getSWR(key) {
    const item = this.cache.get(key);
    if (!item) return { data: null, isStale: false, hasData: false };

    const age = Date.now() - item.timestamp;
    const isStale = age > item.ttl;
    const isDead = age > item.ttl + SWR_GRACE_PERIOD_MS;

    if (isDead) {
      this.delete(key);
      return { data: null, isStale: false, hasData: false };
    }

    return {
      data: item.data,
      isStale,
      hasData: true,
    };
  }

  /**
   * Store data in cache (and sessionStorage for persistent resources)
   */
  set(key, data, customTTL) {
    const ttl = customTTL || this.getDefaultTTL(key);
    const entry = {
      data,
      timestamp: Date.now(),
      ttl,
    };

    this.cache.set(key, entry);

    if (this.isPersistentResource(key) && typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
      } catch (e) {
        // Handle storage quota exceeded gracefully
        console.warn('[ApiCache] SessionStorage quota exceeded, caching in memory only.');
      }
    }

    this.notifyListeners(key);
  }

  /**
   * Remove a specific key
   */
  delete(key) {
    this.cache.delete(key);
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        sessionStorage.removeItem(STORAGE_PREFIX + key);
      } catch {}
    }
    this.notifyListeners(key);
  }

  /**
   * Automatically invalidate cached resources when mutations happen
   */
  invalidateByUrl(mutationUrl = '') {
    const clean = mutationUrl.toLowerCase();
    const prefixesToPurge = [];

    if (clean.includes('/leads') || clean.includes('/enquiry')) {
      prefixesToPurge.push('/leads', '/onboarding', '/dashboard');
    }
    if (clean.includes('/surgeries') || clean.includes('/appointments')) {
      prefixesToPurge.push('/surgeries', '/appointments', '/dashboard');
    }
    if (clean.includes('/onboarding')) {
      prefixesToPurge.push('/onboarding');
    }
    if (clean.includes('/users') || clean.includes('/roles')) {
      prefixesToPurge.push('/users', '/roles');
    }
    if (clean.includes('/hospitals')) {
      prefixesToPurge.push('/hospitals');
    }
    if (clean.includes('/payouts') || clean.includes('/payments')) {
      prefixesToPurge.push('/payouts', '/payments', '/dashboard');
    }

    if (prefixesToPurge.length === 0) {
      const rootSegment = clean.split('?')[0].split('/')[1];
      if (rootSegment) {
        prefixesToPurge.push(`/${rootSegment}`);
      }
    }

    let purgedCount = 0;
    for (const cachedKey of this.cache.keys()) {
      const match = prefixesToPurge.some((prefix) => cachedKey.toLowerCase().includes(prefix));
      if (match) {
        this.delete(cachedKey);
        purgedCount++;
      }
    }

    if (purgedCount > 0) {
      this.notifyListeners('*');
    }
  }

  /**
   * Clear all cached data
   */
  clear() {
    this.cache.clear();
    this.inFlight.clear();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const k = sessionStorage.key(i);
          if (k && k.startsWith(STORAGE_PREFIX)) keysToRemove.push(k);
        }
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
      } catch {}
    }
    this.notifyListeners('*');
  }

  /**
   * Observer subscriptions for React hooks
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notifyListeners(key) {
    for (const listener of this.listeners) {
      try {
        listener(key);
      } catch (err) {
        console.error('[ApiCache] Listener error:', err);
      }
    }
  }
}

export const apiCache = new ApiCacheManager();
export default apiCache;
